import { objectEntries, valueof } from 'utils/typeUtils';

import type { DefaultTheme } from 'styled-components';

const color = {
  common: { white: '#fff', black: '#000', transparent: 'transparent' },
  gray: {
    50: '#FBFBFB',
    200: '#F3F3F3',
    400: '#C1C2C3',
    700: '#696969',
    900: '#282828',
    950: '#F6F6F6',
  },
  navy: {
    50: '#E6E7F0',
    300: '#7275AA',
    700: '#2F2D75',
    900: '#110C39',
  },
  blue: {
    25: '#EFFBFE',
    200: '#81DEF5',
    600: '#00AECF',
    900: '#00626E',
  },
  green: {
    50: '#E6F6EE',
    300: '#66C4AB',
    600: '#399A7C',
    900: '#235C44',
  },
  sunrise: {
    50: '#FEF8E1',
    200: '#FDE283',
    500: '#FCC40B',
    800: '#FD9200',
  },
  sunset: {
    25: '#FFF5F7',
    200: '#F89FA1',
    400: '#FF595A',
    800: '#D72D30',
  },
} as const;

export type GetColorOptions<T> = {
  readonly [P in keyof T]: keyof T[P];
};

export type GetColorList<T> = {
  readonly [P in keyof T]: `${P extends number | string ? P : never}-${keyof T[P] extends string | number
    ? keyof T[P]
    : never}`;
}[keyof T];

export const globalValues = ['inherit', 'initial', 'revert', 'revert-layer', 'unset'] as const;
export type GlobalValues = (typeof globalValues)[number];

export type ColorObject = typeof color;
export type ColorOptions = GetColorOptions<ColorObject>;

export type ColorList = GetColorList<ColorObject> | GlobalValues;
export type ColorDefinition = keyof ColorObject;
export const colorListArray = objectEntries(color)
  .map(([colorName, colorValues]) => Object.keys(colorValues).map(value => `${colorName}-${value}`))
  .flat() as ColorList[];

export interface LightAndDark {
  light: ColorList;
  dark: ColorList;
}

type Common = ColorOptions['common'];
type Others = valueof<Omit<ColorOptions, 'common'>>;
type ColorArray = [ColorDefinition, Common | Others];

export type GetColorDefinition = ColorList | LightAndDark;

export const getColor = (palette: DefaultTheme['palette'], colors: GetColorDefinition) => {
  if (!colors || !palette) {
    return;
  }

  if (typeof colors === 'string' && globalValues.some(value => value === colors)) {
    return colors;
  }
  const [colorKey, colorValue] =
    typeof colors === 'string' ? (colors.split('-') as ColorArray) : (colors[palette.mode].split('-') as ColorArray);
  const subPalette = palette[colorKey] || palette.common;

  return 'white' in subPalette
    ? subPalette[(colorValue as Common) || (colorKey as Common)]
    : subPalette[colorValue as Others];
};

export default color;
